import React from 'react';
import PropTypes from 'prop-types';
import { Tag } from 'antd';
import { toMetrics } from './toMetrics';
import { floor } from '../../../../../../util/lodashlike';
import { normalizeAlertScore } from '../../../../../../util/normalization';
import style from './index.scss';

/* Backend alertLevel -> tone, and tone -> the two class families below
   (FIN-14138).

   KEYED OFF alertLevel, NEVER OFF THE NUMBER. The band cut-offs are
   tenant-configurable and this component is not told what they are, so
   deriving a colour from the score would hardcode a policy we do not know —
   the same reason FpAlertHeader colours its bar this way (its index.scss:122).
   The score ITSELF used to be painted `$danger` unconditionally in the
   stylesheet, so a Low alert rendered a red 84.31 and a red bar beside its own
   green "Low" badge: one widget stating both the most and the least severe
   band. The badge was always right; it is the only part that read this map.

   `Good` is the middle band's name on the configured QA tenant (Low 80-89 /
   Good 90-99 / High 100). Without it the level fell through to the neutral
   default, so a 90-99 alert badged GREY. Alias it to the Medium tone rather
   than inventing a fourth: it is the same rung of the ladder under a different
   name. An unrecognised name still resolves to Default and badges neutral —
   never borrowing a hue that would assert a band we were not told. */
const LEVEL_TONE = {
  High: 'High',
  Good: 'Medium',
  Medium: 'Medium',
  Low: 'Low'
};

// `#RRGGBB` only — the one form the badge's tint can be built from. See tagStyle.
const HEX6 = /^#[0-9a-f]{6}$/i;

const capitalize = (s) =>
  typeof s === 'string' && s.length
    ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
    : s;

/**
 * Right-hand metrics strip on the Spatial header card:
 *
 *    2                1                ALERT SCORE 82.00/100  [High]
 *    VARIANTS         ACTION           ▓▓▓▓▓▓▓▓▓▓▓▓▓▓░░░░░░
 *    BREACHED         REJECTED
 *
 * Same numbers as Classic's counter tiles (see toMetrics), re-laid-out to the
 * Spatial design: one bordered strip of cells rather than separate cards.
 *
 * Renders NOTHING when there is nothing to say. An empty bordered box beside the
 * alert id reads as a component that failed to load; absent, the header simply
 * ends after the controls, which is what today's live data produces.
 */
const AlertMetrics = ({ alertDetails }) => {
  const {
    variantsBreached,
    actions,
    alertScore,
    alertLevel,
    alertLevelLabel,
    alertLevelColor
  } = toMetrics(alertDetails);

  const hasCounters = actions.length > 0;
  const hasScore = alertScore != null;
  if (!hasCounters && !hasScore) return null;

  // Cells first, dividers interleaved — the design separates them with real 1px
  // elements (`div[data-orientation="vertical"]`), not borders, so a divider only
  // ever appears BETWEEN two rendered cells and never dangles at an edge.
  const cells = [];

  if (hasCounters) {
    cells.push(
      <div className={`${style.cell} ${style.cellVariants}`} key="variants">
        <div className={style.figure}>{variantsBreached}</div>
        <div className={style.caption}>
          Variants
          <br />
          Breached
        </div>
      </div>
    );
  }

  // One cell per action. Almost always exactly one ("REJECTED"), but the summary
  // is a map and a rule set can mix actions, so this is not hard-coded to the
  // first key.
  actions.forEach((action) => {
    cells.push(
      <div className={`${style.cell} ${style.cellAction}`} key={action.name}>
        <div className={`${style.figure} ${style.figureAction}`}>
          {action.count}
        </div>
        {/* Two lines, two colours: "Action" is muted like every other caption,
            the action name itself is foreground. */}
        <div className={style.caption}>
          Action
          <br />
          <span className={style.actionName}>{action.name}</span>
        </div>
      </div>
    );
  });

  // Score is gated exactly as Classic gates it (`data?.alertScore != null`).
  // Live FRAML-P alerts carry no alertScore, so this block is invisible there —
  // the same as Classic today, not a Spatial regression.
  if (hasScore) {
    const numeric = Number(alertScore);
    // Resolve the KEY first, then index `style`. `style[undefined]` looks falsy
    // with real CSS Modules but returns the truthy string "undefined" under
    // identity-obj-proxy, so a `style[map[x]] || style.fallback` form would take
    // the fallback in production and silently skip it in tests.
    /* THE CONFIGURED COLOUR WINS, the class ladder is only the fallback.
       `alertLevelColor` is the tenant's own hue for this level and is what
       Classic paints with, so taking it here is what stops the two views
       disagreeing — not a second ladder of our own that happens to agree today.
       Inline beats any class, so when the field is present nothing below it
       matters; when it is absent (older payload, or a level with no colour
       configured) the `.tone*` ladder still bands the score rather than
       rendering it one flat colour. */
    const toneKey = LEVEL_TONE[capitalize(alertLevel)] || 'Default';
    // Badge plate and score tone from ONE resolved key, so the two halves of
    // this widget cannot disagree about the band again.
    const levelClass = `level${toneKey}`;
    const toneClass = `tone${toneKey}`;
    const figureStyle = alertLevelColor
      ? { color: alertLevelColor }
      : undefined;
    /* THE PLATE IS ALL-OR-NOTHING, because its tint is BUILT from the colour
       rather than taken from it: `#DF6057` + `26` is the design's 15% alpha as
       an 8-digit hex. That only holds for a 6-digit hex — `red` or `#FFF` would
       concatenate into an invalid value, the browser would drop the background
       alone, and the badge would render the configured colour's TEXT on the
       fallback class's PLATE. A hue mismatch inside one badge is a smaller
       version of the defect this ticket is about, so an unrecognised format
       hands the whole badge back to the class instead of half of it.
       Configured values are `#RRGGBB` today (alert_level_details); this guards
       the day one is not. The figure and the bar need no such guard — they take
       the colour as given, with nothing appended. */
    const tagStyle = HEX6.test(alertLevelColor || '')
      ? { color: alertLevelColor, background: `${alertLevelColor}26` } // 15%
      : undefined;
    cells.push(
      <div
        className={`${style.cell} ${style.cellScore} ${style[toneClass]}`}
        key="score">
        {/* `justify-between`: label left, the whole value group right. That
            needs the group to be ONE child — as four siblings they would
            spread across the row instead. */}
        <div className={style.scoreHead}>
          <span className={style.scoreLabel}>Alert Score</span>
          <span className={style.scoreValueGroup}>
            {/* Two decimals, as the design renders it ("82.00/100"). Falls
                back to the raw value if the BE sends something non-numeric. */}
            <span className={style.scoreValue} style={figureStyle}>
              {/* CLASSIC'S ARITHMETIC (FIN-13561). `toFixed` PADS, so a whole
                  score read 85.00 here and 85 in Classic on the same alert —
                  the ticket's FRAML-P case, present on this header too.
                  `floor(x, 2)` truncates without padding and
                  `normalizeAlertScore` clamps to 0..100; both are the helpers
                  SmartAssessmentTopPanel:529 uses, shared rather than copied so
                  the three headers cannot drift apart again. */}
              {Number.isFinite(numeric)
                ? normalizeAlertScore(floor(numeric, 2))
                : alertScore}
            </span>
            <span className={style.scoreMax}>/100</span>
            {alertLevel ? (
              <Tag
                className={`${style.levelTag} ${style[levelClass]}`}
                style={tagStyle}>
                {alertLevelLabel || capitalize(alertLevel)}
              </Tag>
            ) : null}
          </span>
        </div>
        {/* Proportion of the 0-100 scale, clamped: a score outside the range
            would otherwise render a bar wider than its track. */}
        <div className={style.scoreTrack}>
          <div
            className={style.scoreFill}
            style={{
              width: `${Math.max(
                0,
                Math.min(100, Number.isFinite(numeric) ? numeric : 0)
              )}%`,
              // Spread LAST so an absent colour leaves the `.tone*` fallback in
              // place — `background: undefined` would also do that, but only by
              // accident of React dropping it.
              ...(alertLevelColor ? { background: alertLevelColor } : {})
            }}
          />
        </div>
      </div>
    );
  }

  /* FIN-14024 — THE COUNTERS GRID AND THE SCORE ARE SIBLINGS, NOT ONE ROW.
     One cell per action and `TRIGGERED_RULES_ACTION_SUMMARY` has no fixed size,
     so a three-action alert (live on keycloak-qa: Soft Stop / Stop / Hard Stop)
     made the single flex row too wide to sit beside the header fields. It
     wrapped to its own line and `margin-left: auto` then pushed it hard right,
     leaving the wide empty band QA reported.

     Counters now wrap INTERNALLY in a two-column grid instead of lengthening the
     strip, and the score sits beside that grid rather than inside it — which is
     also what keeps the grid's parity stable, since the score would otherwise
     flip every count from odd to even.

     NO DIVIDER ELEMENTS. The 1px rules are gaps in the grid with the container's
     border colour showing through. They used to be real `<div>`s, and they must
     not come back: `:nth-child` counts them even at `display: none`, which
     shifts every cell's position by one and breaks the odd-tile rule below. */
  const scoreCell = cells.find((c) => c.key === 'score');
  const counterCells = cells.filter((c) => c.key !== 'score');

  return (
    <div className={style.metricsOuter}>
      <div className={style.metricsGroup}>
        {counterCells.length ? (
          <div className={style.metrics}>{counterCells}</div>
        ) : null}
        {scoreCell}
      </div>
    </div>
  );
};

AlertMetrics.propTypes = {
  alertDetails: PropTypes.object
};

export default AlertMetrics;
